import React, { useEffect, useRef, useState } from 'react';
import { defineMessages, FormattedMessage, injectIntl, RawIntlProvider } from 'react-intl';
import { Overlay, Popover } from 'react-bootstrap';
import classnames from 'classnames';
import './FraResult.css';
import CheckCircle from 'components/CheckCircle';
import moment from 'moment';
import InputCalendar, { isDisabledDate } from 'components/InputCalendar';


const FraResult = (props) => {
	
	moment.locale(props.userLocale);
	const inspectionPassRef = useRef();
	const inspectionDateRef = useRef();
	const attestRef = useRef();
	const signatureOverlayRef = useRef();
	const signatureRef = useRef();

	const handlePassCheckCircleClick = (name, checked) => {
		if (!checked)
			props.setFormData('hasInspectionPassed', true);
		else
			props.setFormData('hasInspectionPassed', null);
	}

	const handleFailCheckCircleClick = (name, checked) => {
		if (!checked) {
			props.setFormData('hasInspectionPassed', false);
			props.setFormData('inspectorAttested', false);

			if (props.onInspectionFailClick)
				props.onInspectionFailClick();
		}
		else
			props.setFormData('hasInspectionPassed', null);
	}

	const onClickAttest = () => {
		props.setFormData('inspectorAttested',
			!props.formData.get('inspectorAttested'));
	}

	const handleChange = (event) => {
		event.preventDefault();
		props.setFormData(event.currentTarget.id, event.currentTarget.value);
	}

	const onDateChangeHandler = (selectedDay, isDisabledFromCalendar = false) => {
		const date = selectedDay !== undefined ? new Date(moment(selectedDay)) : undefined;
        const isDisabled = isDisabledFromCalendar || isDisabledDate(date, modifiers.disabled);

		props.onInspectionDateChangeHandler(selectedDay, isDisabled);
		if (isDisabled || !selectedDay) {
			props.setFormData('inspectionDate', null);
			return;
		}
		else {
			props.setFormData('inspectionDate', selectedDay);
		}
	}

		//#region style
		const modifiers={
			today: {},
			disabled:{
				before: new Date(moment(props.poCreationDate).subtract(90, 'days')),
				after: new Date(moment().subtract(0, 'days')),
			}
		}
		//#endregion

		const messages = defineMessages({
			fra_inspection__pass: {
				id: 'fra_inspection__pass',
				description: 'Inspection pass label',
				defaultMessage: 'PASS'
			},
			fra_inspection__fail: {
				id: 'fra_inspection__fail',
				description: 'Inspection fail label',
				defaultMessage: 'FAIL'
			},
			fra_inspection__inspection_date: {
				id: 'fra_inspection__inspection_date',
				description: 'Inspection date label',
				defaultMessage: 'Inspection date'
			},
			fra_inspection__inspection_date_invalid: {
				id: 'fra_inspection__inspection_date_invalid',
				description: 'Inspection date is invalid/incorrectly formatted',
				defaultMessage: 'Date invalid/incorrectly formatted, please enter a valid date'
			},
			fra_inspection__inspection_date_disabled: {
				id: 'fra_inspection__inspection_date_disabled',
				description: 'Inspection date disabled/out of range',
				defaultMessage: 'Please enter a valid date'
			},
			fra_inspection__has_inspection_passed_required: {
				id: 'fra_inspection__has_inspection_passed_required',
				description: 'Inspection pass/label label required',
				defaultMessage: 'Please select PASS or FAIL'
			},
			fra_inspection__has_inspection_passed_components_invalid: {
				id: 'fra_inspection__has_inspection_passed_components_invalid',
				description: 'Inspection passed but all items are not passed',
				defaultMessage: 'All items need to be marked as OK to pass inspection'
			},
			fra_inspection__has_inspection_passed_unsafe_conditions: {
				id: 'fra_inspection__has_inspection_passed_unsafe_conditions',
				description: 'Inspection passed but unsafe conditions was filled out',
				defaultMessage: 'The inspection cannot pass with unsafe conditions listed'
			},
			fra_inspection__has_inspection_passed_no_failure_specified: {
				id: 'fra_inspection__has_inspection_passed_no_failure_specified',
				description: 'Inspection failed but no item is marked as failed',
				defaultMessage: 'Please specify failed items'
			},
			fra_inspection__inspector_attest: {
				id: 'fra_inspection__inspector_attest',
				description: 'Inspector attested label',
				defaultMessage: 'I attest that I am qualified to perform the requested inspection and that this vehicle has passed all the inspection items in accordance with the FRA requirements.'
			},
			fra_inspection__attest_required: {
				id: 'fra_inspection__attest_required',
				description: 'Validation: inspector did not attest',
				defaultMessage: 'Please attest to this statement'
			},
			fra_inspection__inspector_signature: {
				id: 'fra_inspection__inspector_signature',
				description: 'Inspector signature label',
				defaultMessage: "Inspector's signature"
			},
			fra_inspection__inspector_signature_note: {
				id: 'fra_inspection__inspector_signature_note',
				description: 'Inspector signature note',
				defaultMessage: "By typing your name, you agree this will serve as the electronic respresentation of your signature."
			},
			fra_inspection__signature_required: {
				id: 'fra_inspection__signature_required',
				description: 'Validation: inspector signature required',
				defaultMessage: 'Please enter your signature'
			},
		});

		return (
			<div>

				<div className="row no-gutters fra_result__result_code" >

					<div className="col-sm-2 no-gutters-col">
						<div className='fra_result__field_container' tabIndex='0'>
							<CheckCircle
								name='hasInspectionPassedTrue'
								checked={props.formData.get('hasInspectionPassed') === true}
								message={<FormattedMessage {...messages.fra_inspection__pass} />}
								onClick={handlePassCheckCircleClick} />
						</div>
					</div>

					<div className="col-sm-2 no-gutters-col" ref={inspectionPassRef}>
						<div className='fra_result__field_container' tabIndex='0'>
							<CheckCircle
								name='hasInspectionPassedFalse'
								checked={props.formData.get('hasInspectionPassed') === false}
								onClick={handleFailCheckCircleClick}
								message={
									<FormattedMessage {...messages.fra_inspection__fail} />
								}
							/>

							{!!props.formValidationMessages.get('hasInspectionPassed') &&
								<Overlay
									show="true"
									target={inspectionPassRef.current}
									placement="right">
									<Popover
										id='hasInspectionPassedOverlay'
										className='fra_result__error_message'
									>
										<RawIntlProvider value={props.intl}>
											<FormattedMessage {...(messages[props.formValidationMessages.get('hasInspectionPassed')])} />
										</RawIntlProvider>
									</Popover>
								</Overlay>
							}
						</div>
					</div>

				</div>

				<div className='fra_result__inspection_date'>
					<div className='fra_result__field_container' tabIndex='0'>
						<div>
							<FormattedMessage {...messages.fra_inspection__inspection_date} />
						</div>
						<div className='fra_result__date_input' ref={inspectionDateRef}>
							<InputCalendar
							modifiers={modifiers}
							initialValue={props.formData.get('inspectionDate')}
							onDateChange={onDateChangeHandler}
							locale={props.userLocale}
							/>
							{!!props.formValidationMessages.get('inspectionDate') &&
								<Overlay
									show="true"
									target={inspectionDateRef.current}
									placement="auto">
									<Popover
										id='inspectionDateOverlay'
										className='fra_result__error_message'
									>
										<RawIntlProvider value={props.intl}>
											<FormattedMessage {...(messages[props.formValidationMessages.get('inspectionDate')])} />
										</RawIntlProvider>
									</Popover>
								</Overlay>
							}
						</div>
					</div>
				</div>

				{props.formData.get('hasInspectionPassed') !== false &&
					<div className='fra_result__attest' ref={attestRef}>
						<div className='fra_result__field_container' tabIndex='0'>
							<CheckCircle checked={props.formData.get('inspectorAttested')}
								name='inspectorAttested'
								message={<FormattedMessage {...messages.fra_inspection__inspector_attest} />}
								disabled={props.formData.get('hasInspectionPassed') === false}
								onClick={onClickAttest}
							/>
							{!!props.formValidationMessages.get('inspectorAttested') &&
								<Overlay
									show="true"
									target={attestRef.current}
									placement="bottom"
								>
									<Popover
										id='inspectorAttestedOverlay'
										className='fra_result__error_message'
									>
										<RawIntlProvider value={props.intl}>
											<FormattedMessage {...(messages[props.formValidationMessages.get('inspectorAttested')])} />
										</RawIntlProvider>
									</Popover>
								</Overlay>
							}
						</div>
					</div>
				}

				<div className='fra_result__inspector_signature' ref={signatureRef}>
					<div className='fra_result__field_container'>
						<FormattedMessage {...messages.fra_inspection__inspector_signature} />
						{' '}
						<input
							id='inspectorSignature'
							maxLength='50'
							value={props.formData.get('inspectorSignature')}
							onChange={handleChange}
							ref={signatureOverlayRef}
						/>
						<div className='fra_result__inspector_signature_note'>
							<FormattedMessage {...messages.fra_inspection__inspector_signature_note} />
						</div>
						{!!props.formValidationMessages.get('inspectorSignature') &&
							<Overlay
								show="true"
								target={signatureOverlayRef.current}
								placement="bottom">
								<Popover
									id='inspectorSignatureOverlay'
									className='fra_result__error_message'
								>
									<RawIntlProvider value={props.intl}>
										<FormattedMessage {...(messages[props.formValidationMessages.get('inspectorSignature')])} />
									</RawIntlProvider>
								</Popover>
							</Overlay>
						}
					</div>
				</div>

			</div>
		)
};

export default injectIntl(FraResult);
